import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';
import { TAdminDetails } from '~types/feat/admin';

import Table from '~components/molecules/Table';
import ActionButton from '~components/atoms/ActionButton';

import UtilFunctions from '~utils/functions';
import { AuthUserStatusEnum, ModuleEnum, PermissionsEnum } from '~utils/enums';

import * as Styles from './_adminsTable.module.scss';

const AdminsTable = () => {
    const {
        Misc:
        {
            dateTimeFormat,
            renderBinaryStatusChip,
        },
    } = UtilFunctions;

    const adminListingIndex = useAppSelector(state => state.admin.adminListing.index);
    const allBranchesList = useAppSelector(state => state.branch.allBranches);

    const isLoading = useAppSelector(state => state.admin.loadings.getAdminListing);
    const adminListing = useAppSelector(state => state.admin.adminListing.data);
    const access = useAppSelector(state => state.auth.userDetails.access);

    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.RBAC);

    useEffect(() => {
        dispatch(combinedActions.adminGetAdminListingAttempt());
    }, [adminListingIndex]);

    const dispatch = useAppDispatch();

    const setAdminDetailsModalIsOpen = (isOpen: boolean) => dispatch(combinedActions.adminSetAdminDetailsModalIsOpen(isOpen));
    const setDeleteAdminModalIsOpen = (isOpen: boolean) => dispatch(combinedActions.adminSetDeleteAdminModalIsOpen(isOpen));
    const setIsEditing = (isEditing: boolean) => dispatch(combinedActions.adminSetAdminDetailsModalIsEditing(isEditing));
    const getAdminDetails = (adminDetails: TAdminDetails) => dispatch(combinedActions.adminSetAdminDetails(adminDetails));

    const handleViewAdminDetailsClick = (adminDetails: TAdminDetails) => {
        setIsEditing(false);
        setAdminDetailsModalIsOpen(true);
        getAdminDetails(adminDetails);
    };

    const handleEditAdminClick = (adminDetails: TAdminDetails) => {
        setIsEditing(true);
        setAdminDetailsModalIsOpen(true);
        getAdminDetails(adminDetails);
    };

    const handleDeleteAdminClick = (adminDetails: TAdminDetails) => {
        setIsEditing(false);
        setDeleteAdminModalIsOpen(true);
        getAdminDetails(adminDetails);
    };

    const tableHeader = [
        'USER ID',
        'SAM ACCOUNT NAME',
        'DISPLAY NAME',
        'EMAIL',
        'BRANCH CODE',
        'BRANCH NAME',
        'ROLE NAME',
        'REFERRAL CODE',
        'STATUS',
        'CREATED AT',
        'UPDATED AT',
        'LAST LOGIN AT',
        'ACTION',
    ];

    const tableBody = adminListing.map(admin => {
        const {
            userId,
            sAMAccountName,
            displayName,
            branchCode,
            roleName,
            email,
            referralCode,
            status,
            createdAt,
            updatedAt,
            lastLoginAt,
        } = admin;

        const branchName = allBranchesList.find(branch => branch.branchCode === branchCode)?.branchName || '';

        const massagedData = [
            userId,
            sAMAccountName,
            displayName,
            email,
            branchCode,
            branchName,
            roleName,
            referralCode,
            status ? renderBinaryStatusChip(status, AuthUserStatusEnum[status]) : '-',
            createdAt ? dateTimeFormat(createdAt) : '-',
            updatedAt ? dateTimeFormat(updatedAt) : '-',
            lastLoginAt ? dateTimeFormat(lastLoginAt) : '-',
        ];

        return [
            ...massagedData,
            (
                <div
                    key={userId}
                    className={Styles['button-container']}
                >
                    <ActionButton
                        label='View'
                        onClick={() => handleViewAdminDetailsClick(admin)}
                    />

                    <>
                        {hasEditAccess
                            && (
                                <ActionButton
                                    label='Edit'
                                    onClick={() => handleEditAdminClick(admin)}
                                />
                            )
                        }

                        {(hasEditAccess && status !== AuthUserStatusEnum.Deactivated)
                            && (
                                <ActionButton
                                    label='Delete'
                                    onClick={() => handleDeleteAdminClick(admin)}
                                    className={Styles.danger}
                                />
                            )
                        }
                    </>
                </div>
            ),
        ];
    });

    return (
        <Table
            isLoading={isLoading}
            header={tableHeader}
            body={tableBody}
        />
    );
};

export default AdminsTable;
